// The three transaction families, as data.
//
// Commission, clawback and payout transactions are near-identical screens. The
// previous module had three copy-pasted slices; this table is the single place
// their differences live, and one generic container, one reducer factory and one
// saga generator read from it.
//
// The API functions deliberately live in saga.es6.js, NOT here: importing
// requests.es6 from this module closes an import cycle
// (families -> requests -> access -> store -> rootSaga -> saga -> actions ->
// families), which leaves FAMILIES undefined depending on module init order.
//
// The differences are real, not cosmetic:
//   - payout requires policy_type and rejects the policy/product filters
//   - each family names its amount field differently
//   - the three statistics endpoints share almost no field names (Gap 11), so
//     each family's cards are typed exactly from the spec rather than unified

import * as types from '../../lib/actionTypes.es6';

export var FAMILIES = [
  {
    key: 'commission',
    label: 'Commission',
    amountField: 'commission_amount_in_cents',
    ruleField: 'commission_rule_guid',
    // Filters this endpoint actually accepts.
    supportsPolicyFilters: true,
    requiresPolicyType: false,
    types: {
      list: {
        request: types.GET_COMMISSION_TRANSACTIONS_REQUEST,
        success: types.GET_COMMISSION_TRANSACTIONS_SUCCESS,
        failure: types.GET_COMMISSION_TRANSACTIONS_FAILURE
      },
      detail: {
        request: types.GET_COMMISSION_TRANSACTION_BY_GUID_REQUEST,
        success: types.GET_COMMISSION_TRANSACTION_BY_GUID_SUCCESS,
        failure: types.GET_COMMISSION_TRANSACTION_BY_GUID_FAILURE
      },
      byAgent: {
        request: types.GET_COMMISSION_TRANSACTIONS_BY_AGENT_REQUEST,
        success: types.GET_COMMISSION_TRANSACTIONS_BY_AGENT_SUCCESS,
        failure: types.GET_COMMISSION_TRANSACTIONS_BY_AGENT_FAILURE
      },
      stats: {
        request: types.GET_COMMISSION_TXN_STATS_REQUEST,
        success: types.GET_COMMISSION_TXN_STATS_SUCCESS,
        failure: types.GET_COMMISSION_TXN_STATS_FAILURE
      }
    },
    // Field names taken verbatim from the spec. Deliberately NOT unified with
    // the other families — see Gap 11.
    statCards: [
      { key: 'total_commissions_in_cents', label: 'Total', kind: 'money' },
      { key: 'total_paid_commissions_in_cents', label: 'Paid', kind: 'money' },
      {
        key: 'total_pending_commissions_in_cents',
        label: 'Pending',
        kind: 'money'
      },
      {
        key: 'total_failed_commissions_in_cents',
        label: 'Failed',
        kind: 'money',
        tone: 'error'
      }
    ]
  },

  {
    key: 'clawback',
    label: 'Clawback',
    amountField: 'original_clawback_amount_in_cents',
    // What is still to be deducted once part of the clawback has been
    // recovered from later commission.
    detailMoneyFields: [
      {
        key: 'remaining_clawback_amount_in_cents',
        label: 'Remaining to claw back'
      }
    ],
    ruleField: 'clawback_rule_guid',
    supportsPolicyFilters: true,
    requiresPolicyType: false,
    types: {
      list: {
        request: types.GET_CLAWBACK_TRANSACTIONS_REQUEST,
        success: types.GET_CLAWBACK_TRANSACTIONS_SUCCESS,
        failure: types.GET_CLAWBACK_TRANSACTIONS_FAILURE
      },
      detail: {
        request: types.GET_CLAWBACK_TRANSACTION_BY_GUID_REQUEST,
        success: types.GET_CLAWBACK_TRANSACTION_BY_GUID_SUCCESS,
        failure: types.GET_CLAWBACK_TRANSACTION_BY_GUID_FAILURE
      },
      byAgent: {
        request: types.GET_CLAWBACK_TRANSACTIONS_BY_AGENT_REQUEST,
        success: types.GET_CLAWBACK_TRANSACTIONS_BY_AGENT_SUCCESS,
        failure: types.GET_CLAWBACK_TRANSACTIONS_BY_AGENT_FAILURE
      },
      stats: {
        request: types.GET_CLAWBACK_TXN_STATS_REQUEST,
        success: types.GET_CLAWBACK_TXN_STATS_SUCCESS,
        failure: types.GET_CLAWBACK_TXN_STATS_FAILURE
      }
    },
    // This family reports counts as well as money — a different shape entirely.
    statCards: [
      { key: 'total_transactions', label: 'Transactions', kind: 'count' },
      {
        key: 'total_clawback_amount_in_cents',
        label: 'Total clawed back',
        kind: 'money'
      },
      {
        key: 'average_clawback_amount_in_cents',
        label: 'Average',
        kind: 'money'
      },
      { key: 'pending_transactions', label: 'Pending', kind: 'count' },
      { key: 'processed_transactions', label: 'Processed', kind: 'count' },
      { key: 'deducted_transactions', label: 'Deducted', kind: 'count' }
    ]
  },

  {
    key: 'payout',
    label: 'Payout',
    amountField: 'payout_amount_in_cents',
    ruleField: null,
    // This endpoint does NOT accept policy_guid / policy_number / product_code,
    // and REQUIRES policy_type (Gap 4).
    supportsPolicyFilters: false,
    requiresPolicyType: true,
    types: {
      list: {
        request: types.GET_PAYOUT_TRANSACTIONS_REQUEST,
        success: types.GET_PAYOUT_TRANSACTIONS_SUCCESS,
        failure: types.GET_PAYOUT_TRANSACTIONS_FAILURE
      },
      detail: {
        request: types.GET_PAYOUT_TRANSACTION_BY_GUID_REQUEST,
        success: types.GET_PAYOUT_TRANSACTION_BY_GUID_SUCCESS,
        failure: types.GET_PAYOUT_TRANSACTION_BY_GUID_FAILURE
      },
      byAgent: {
        request: types.GET_PAYOUT_TRANSACTIONS_BY_AGENT_REQUEST,
        success: types.GET_PAYOUT_TRANSACTIONS_BY_AGENT_SUCCESS,
        failure: types.GET_PAYOUT_TRANSACTIONS_BY_AGENT_FAILURE
      },
      stats: {
        request: types.GET_PAYOUT_TXN_STATS_REQUEST,
        success: types.GET_PAYOUT_TXN_STATS_SUCCESS,
        failure: types.GET_PAYOUT_TXN_STATS_FAILURE
      }
    },
    // Note this family reuses the *_commissions_* names for payouts, and has no
    // grand total at all.
    statCards: [
      { key: 'total_paid_commissions_in_cents', label: 'Paid', kind: 'money' },
      {
        key: 'total_pending_commissions_in_cents',
        label: 'Pending',
        kind: 'money'
      },
      {
        key: 'total_failed_commissions_in_cents',
        label: 'Failed',
        kind: 'money',
        tone: 'error'
      }
    ]
  }
];

export function familyByKey(key) {
  for (var i = 0; i < FAMILIES.length; i += 1) {
    if (FAMILIES[i].key === key) return FAMILIES[i];
  }
  return FAMILIES[0];
}
